import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { m as motion, useTransform, useViewportScroll } from 'framer-motion';
import { useWindowSize } from 'react-use';
import { IconArrowsSort, IconCornerDownLeft } from '@tabler/icons';

import imgThemes from '../../../../assets/themes.svg';
import { useBreakpoint } from '../../../../hooks';
import { Feature } from '../../../../components';

function FeatureThemeInfo() {
  const { scrollY } = useViewportScroll();
  const opacity = useTransform(scrollY, [3700, 3800, 4600, 4750], [0, 1, 1, 0]);

  return (
    <motion.div class="absolute" style={{ opacity }}>
      <Feature.Name className="text-blue-400">
        <Feature.Icon src={imgThemes} alt="Themes icon" />
        Themes
      </Feature.Name>
      <Feature.Headline className="from-blue-400 to-blue-900 bg-clip-text text-transparent">
        Beautiful themes.
      </Feature.Headline>
      <Feature.Description className="text-gray-400">
        UnTab comes with a bunch of themes to match your preferences and moods.
        Thanks to the open community of UnTab, themes can be contributed by anyone.
      </Feature.Description>
    </motion.div>
  );
}

const RESULTS = [
  {
    favicon: '🌒',
    text: 'Dark',
  },
  {
    favicon: '☀️',
    text: 'Light',
  },
  {
    favicon: '☕',
    text: 'Coffee',
  },
  {
    favicon: '🌿',
    text: 'Spearmint',
  },
  {
    favicon: '🌊',
    text: 'Ocean',
  },
];

function FeatureThemeResult({ theme }) {
  const { height, width } = useWindowSize();
  const { scrollY } = useViewportScroll();
  const themeResultOpacity = useTransform(scrollY, [3800, 4000,  4600, 4750], [0, 1, 1, 0]);
  const themeResultY = useTransform(scrollY, [3800, 4000], [0, -30]);
  const themeResultNavigateY = useTransform(scrollY, [4100, 4200], [0, 57]);
  const themeResultNavigateScale = useTransform(scrollY, [4300, 4315, 4330], [1, 0.95, 1]);
  const bp = useBreakpoint();

  return (
    <>
      <motion.div style={{ opacity: themeResultOpacity, y: themeResultY }} class="absolute top-full mt-1 left-0 right-0 rounded-md bg-gray-700 shadow-xl overflow-hidden">
        <motion.div
          style={{ y: themeResultNavigateY, scale: themeResultNavigateScale }}
          class="absolute bg-blue-700 bg-opacity-50 border border-blue-600 top-8 left-2.5 right-2.5 rounded-lg h-12"
        />
        <div class="relative z-10 text-gray-300">
          <div class="uppercase bg-gray-800 px-5 py-1 text-sm tracking-wider text-gray-500 font-semibold">Themes</div>
          {
            RESULTS.map((result) => (
              <div key={result.text} class="flex px-5 py-3.5 items-center">
                <span class="w-4 h-4 leading-4">{result.favicon}</span>
                <div class="ml-5 flex flex-col">
                  <span class="font-medium text-lg">{result.text}</span>
                </div>
              </div>
            ))
          }
          <div class="border-t border-gray-800 text-gray-400 px-5 py-1.5 font-medium text-xs flex justify-end space-x-4">
            <div class="flex items-center space-x-0.5">
              <IconArrowsSort size={17} className="text-gray-300" />
              <span>to navigate</span>
            </div>
            <div class="flex items-center space-x-0.5">
              <IconCornerDownLeft size={18} className="text-gray-300" />
              <span>to select</span>
            </div>
          </div>
        </div>
      </motion.div>
      {
        typeof window !== 'undefined' && bp.md && createPortal(
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: theme === 'dark' ? 1.5 : 0 }}
            transition={{ duration: 0.8 }}
            class="fixed z-0 top-0 -left-1/2 lg:-top-1/2 md:left-0 rounded-full bg-gray-900"
            style={{ width: Math.max(width, height), height: Math.max(width, height)}}
          />,
          window.document.body,
        )
      }
    </>
  );
}

export {
  FeatureThemeInfo,
  FeatureThemeResult,
};
